import { Injectable, inject, signal, computed, effect, DestroyRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { Subscription, fromEvent } from 'rxjs';
import { MessageService } from '@openng/optimus-ui/api';
import { environment } from '../../../environments/environment';
import { ElectronService } from './electron.service';
import { BrowserNotificationService } from './browser-notification.service';
import { PipService } from '../../shared/services/pip.service';
import { MeetingReminder } from '../models/meeting-reminder.model';
import { ReminderSnoozeScope, SnoozeState } from '../models/snooze-state.model';
import { LiveAssistantAutoStartService } from '../../features/live-assistant/services/live-assistant-auto-start.service';

type MeetingReminderAction = 'CLICKED_START' | 'CLICKED_STOP' | 'DISMISSED';

interface MeetingReminderPayload {
  notificationId: number;
  reminderType: 'START' | 'STOP';
  meetingSummary: string;
  meetingStart: string;
  meetingEnd: string;
  calendarEventId: string;
  calendarProvider: string;
}

@Injectable({
  providedIn: 'root',
})
export class MeetingReminderService {
  private readonly http = inject(HttpClient);
  private readonly electronService = inject(ElectronService);
  private readonly browserNotificationService = inject(BrowserNotificationService);
  private readonly messageService = inject(MessageService);
  private readonly pipService = inject(PipService);
  private readonly destroyRef = inject(DestroyRef);

  readonly activeReminder = signal<MeetingReminder | null>(null);
  readonly reminderQueue = signal<MeetingReminder[]>([]);
  readonly snoozeStatus = signal<SnoozeState | null>(null);
  readonly isConnected = signal(false);
  readonly pendingRequestIds = signal<Set<number>>(new Set());

  readonly hasPendingReminders = computed(() => this.reminderQueue().length > 0);
  readonly getIsPending = (reminderId: number) => this.pendingRequestIds().has(reminderId);

  private eventSource: EventSource | null = null;
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = 10;
  private readonly initialBackoffMs = 1000;
  private reconnectTimeoutId: number | null = null;
  private ticketSubscription: Subscription | null = null;
  private pipUnsubscribe: (() => void) | null = null;
  private pipCloseUnsubscribe: (() => void) | null = null;
  private networkSubscription: Subscription | null = null;
  private isConnecting = false;
  private readonly autoStartService = inject(LiveAssistantAutoStartService);

  constructor() {
    this.setupPipSync();
    this.setupCleanup();
    this.listenForPipClose();
    this.setupNetworkReconnection();
  }

  private setupCleanup(): void {
    this.destroyRef.onDestroy(() => {
      this.pipUnsubscribe?.();
      this.pipCloseUnsubscribe?.();
      this.networkSubscription?.unsubscribe();
      this.disconnect();
    });
  }

  private listenForPipClose(): void {
    if (this.electronService.isElectron) {
      this.pipCloseUnsubscribe = this.electronService.onReminderPipClosed(() => {
        const activeId = this.activeReminder()?.id;
        if (activeId) {
          this.dismissReminder(activeId);
        }
      });
    }
  }

  private setupNetworkReconnection(): void {
    this.networkSubscription = fromEvent(window, 'online').subscribe(() => {
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.log('Network restored: Resetting reconnection attempts and re-establishing SSE connection');
        this.reconnectAttempts = 0;
        this.connect();
      }
    });
  }

  private setupPipSync(): void {
    // Master only: the listener below writes the very signals this effect reads, so a broadcasting
    // slave answers every message with another one. With two or more overlay windows that is an
    // unbounded echo between them — postMessage skips the sender, never the siblings — and
    // reminderQueue arrives as a fresh array, so referential equality can never damp it out.
    if (this.pipService.isMaster()) {
      effect(() => {
        const reminder = this.activeReminder();
        const queue = this.reminderQueue();
        this.pipService.postMessage({
          type: 'MEETING_REMINDER_UPDATE',
          activeReminder: reminder,
          reminderQueue: queue,
        });
      });
    }

    // Listen for state updates from main window (if this is the PiP window)
    this.pipUnsubscribe = this.pipService.onMessage((data: unknown) => {
      const msg = data as { type?: string; activeReminder?: MeetingReminder | null; reminderQueue?: MeetingReminder[] };
      if (msg.type === 'MEETING_REMINDER_UPDATE' && !this.pipService.isMaster()) {
        if (msg.activeReminder !== undefined) {
          this.activeReminder.set(msg.activeReminder);
        }
        if (msg.reminderQueue !== undefined) {
          this.reminderQueue.set(msg.reminderQueue);
        }
      }
    });
  }

  connect(): void {
    if (this.isConnecting) {
      return;
    }

    if (this.eventSource) {
      this.disconnect();
    }

    this.ticketSubscription?.unsubscribe();

    this.isConnecting = true;

    this.ticketSubscription = this.getSSETicket()
      .pipe(map(response => response.ticket))
      .subscribe({
        next: ticket => {
          this.ticketSubscription = null;
          this.connectWithTicket(ticket);
        },
        error: error => {
          this.isConnecting = false;
          this.ticketSubscription = null;
          console.error('Failed to obtain SSE ticket:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Connection Error',
            detail: 'Failed to establish notification connection. Retrying...',
            life: 5000,
          });
          this.handleConnectionError();
        },
      });
  }

  private getSSETicket() {
    return this.http.post<{ ticket: string }>(`${environment.apiUrl}/sse-ticket`, {}, { withCredentials: true });
  }

  private connectWithTicket(ticket: string): void {
    const apiUrl = environment.apiUrl;
    const eventSourceUrl = `${apiUrl}/notifications/stream?ticket=${ticket}`;

    try {
      this.eventSource = new EventSource(eventSourceUrl, { withCredentials: true });

      this.eventSource.addEventListener('MEETING_REMINDER_SENT', (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          if (this.isValidMeetingReminder(data)) {
            this.processMeetingReminder(data);
          } else {
            console.warn('Received an unknown event structure on MEETING_REMINDER_SENT channel:', data);
          }
        } catch (error) {
          console.error('Failed to parse meeting reminder event:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Notification Error',
            detail: 'Failed to process notification. The issue has been logged.',
            life: 5000,
          });
        }
      });

      this.eventSource.addEventListener('AUTO_START_READY', (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          this.autoStartService.handleAutoStartReady(data);
        } catch (error) {
          console.error('Failed to parse AUTO_START_READY event:', error);
        }
      });

      this.eventSource.addEventListener('AUTO_STOP_PROMPT', (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          this.autoStartService.handleAutoStopPrompt(data);
        } catch (error) {
          console.error('Failed to parse AUTO_STOP_PROMPT event:', error);
        }
      });

      this.eventSource.addEventListener('sse-ticket-logout', () => {
        this.disconnect();
      });

      this.eventSource.addEventListener('open', () => {
        this.isConnected.set(true);
        this.reconnectAttempts = 0;
        this.isConnecting = false;
      });

      this.eventSource.addEventListener('error', () => {
        this.isConnecting = false;
        this.handleConnectionError();
      });
    } catch (error) {
      this.isConnecting = false;
      console.error('Failed to create EventSource:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Connection Error',
        detail: 'Failed to establish notification connection. Retrying...',
        life: 5000,
      });
      this.handleConnectionError();
    }
  }

  disconnect(): void {
    this.isConnecting = false;
    this.ticketSubscription?.unsubscribe();
    this.ticketSubscription = null;

    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
    }
    this.isConnected.set(false);
  }

  private handleConnectionError(): void {
    this.isConnected.set(false);

    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      const backoffMs = this.initialBackoffMs * Math.pow(2, this.reconnectAttempts);
      this.reconnectAttempts++;

      this.reconnectTimeoutId = window.setTimeout(() => {
        this.connect();
      }, backoffMs);
    }
  }

  private isValidMeetingReminder(data: unknown): data is MeetingReminderPayload {
    if (!data || typeof data !== 'object') {
      return false;
    }
    const obj = data as Record<string, unknown>;
    return (
      typeof obj['notificationId'] === 'number' &&
      (obj['reminderType'] === 'START' || obj['reminderType'] === 'STOP') &&
      typeof obj['meetingSummary'] === 'string' &&
      typeof obj['meetingStart'] === 'string' &&
      typeof obj['meetingEnd'] === 'string' &&
      typeof obj['calendarEventId'] === 'string' &&
      typeof obj['calendarProvider'] === 'string'
    );
  }

  private processMeetingReminder(data: MeetingReminderPayload): void {
    const reminder: MeetingReminder = {
      id: data.notificationId,
      reminderType: data.reminderType,
      meetingSummary: data.meetingSummary,
      meetingStart: new Date(data.meetingStart),
      meetingEnd: new Date(data.meetingEnd),
      calendarEventId: data.calendarEventId,
      calendarProvider: data.calendarProvider,
    };
    this.handleReminder(reminder);
  }

  private handleReminder(reminder: MeetingReminder): void {
    // Prevent adding duplicate reminders
    if (this.reminderQueue().some(r => r.id === reminder.id) || this.activeReminder()?.id === reminder.id) {
      return;
    }

    if (this.activeReminder() === null) {
      this.activeReminder.set(reminder);

      if (this.electronService.isElectron) {
        this.openPipWindow();
      } else if (document.hidden) {
        void this.browserNotificationService.show(reminder);
      }
    } else {
      // There is already an active reminder, so queue this one.
      this.reminderQueue.update(queue => [...queue, reminder]);
    }
  }

  private openPipWindow(): void {
    this.electronService.openReminderPip('meeting-reminder-pip').catch(error => {
      console.error('Failed to open reminder PiP window:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'PiP Window Error',
        detail: 'Failed to open reminder window. Please check the application logs.',
        life: 5000,
      });
    });
  }

  startMeeting(reminderId: number): void {
    this.sendReminderAction(reminderId, 'CLICKED_START', 'Start Meeting Failed', 'Unable to start the meeting. Please try again.');
  }

  stopReminder(reminderId: number): void {
    this.sendReminderAction(reminderId, 'CLICKED_STOP', 'Stop Reminder Failed', 'Unable to stop the reminder. Please try again.');
  }

  dismissReminder(reminderId: number): void {
    this.sendReminderAction(reminderId, 'DISMISSED', 'Dismiss Reminder Failed', 'Unable to dismiss the reminder. Please try again.');
  }

  private sendReminderAction(reminderId: number, action: MeetingReminderAction, errorSummary: string, errorDetail: string): void {
    if (this.pendingRequestIds().has(reminderId)) return;

    this.pendingRequestIds.update(ids => new Set([...ids, reminderId]));
    this.http.post(`${environment.apiUrl}/meeting-reminders/${reminderId}/action`, { action }, { withCredentials: true }).subscribe({
      next: () => {
        this.clearReminder(reminderId);
      },
      error: error => {
        console.error(`Failed to send reminder action ${action}:`, error);
        this.messageService.add({
          severity: 'error',
          summary: errorSummary,
          detail: errorDetail,
          life: 5000,
        });
      },
      complete: () => {
        this.pendingRequestIds.update(ids => {
          const updated = new Set(ids);
          updated.delete(reminderId);
          return updated;
        });
      },
    });
  }

  snoozeReminder(reminderId: number, scope: ReminderSnoozeScope, calendarEventId?: string, meetingEnd?: string): void {
    if (this.pendingRequestIds().has(reminderId)) return;

    this.pendingRequestIds.update(ids => new Set([...ids, reminderId]));
    this.http
      .post<{
        expiresAt: string | null;
        calendarEventId: string | null;
      }>(`${environment.apiUrl}/meeting-reminders/snooze`, { scope, calendarEventId, meetingEnd }, { withCredentials: true })
      .subscribe({
        next: response => {
          this.snoozeStatus.set({
            scope,
            expiresAt: response.expiresAt ? new Date(response.expiresAt) : null,
            calendarEventId: response.calendarEventId ?? undefined,
          });
          this.clearReminder(reminderId);
        },
        error: error => {
          console.error('Failed to snooze reminder:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Snooze Failed',
            detail: 'Unable to snooze the reminder. Please try again.',
            life: 5000,
          });
        },
        complete: () => {
          this.pendingRequestIds.update(ids => {
            const updated = new Set(ids);
            updated.delete(reminderId);
            return updated;
          });
        },
      });
  }

  private clearReminder(reminderId: number): void {
    if (this.activeReminder()?.id === reminderId) {
      // Promote the next reminder from the queue to be the active one
      const nextInQueue = this.reminderQueue()[0] ?? null;
      this.activeReminder.set(nextInQueue);

      if (nextInQueue) {
        // Remove the newly active reminder from the queue
        this.reminderQueue.update(queue => queue.slice(1));
        // Trigger the notification for the new active reminder
        if (this.electronService.isElectron) {
          this.openPipWindow();
        } else if (document.hidden) {
          void this.browserNotificationService.show(nextInQueue);
        }
      } else {
        // No more reminders, close the PiP window
        if (this.electronService.isElectron) {
          void this.electronService.closeReminderPip();
        }
      }
    } else {
      // The reminder to clear was in the queue, not the active one
      this.reminderQueue.update(queue => queue.filter(r => r.id !== reminderId));
    }
  }
}
